import React, { useMemo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { isEmpty, propEq, length, propOr, sortBy } from 'ramda'
import strings from 'utils/strings'
import { Paragraph, Alert, Header } from 'components/patterns'
import { Row, Col } from 'components/patterns/Layout'
import { TextArea } from 'components/deprecated/Inputs'
import {
  getRemovedSwitches,
  getAddedSwitches,
  getRemovedSwitchesFromPrevSnapshot,
} from 'utils/feature'
import { useSelector, useDispatch } from 'react-redux'
import { getAllSolenoids, solenoidListOnVehicleSelector } from 'selectors/ecp'
import { getTruckFeatures } from 'selectors/truck'
import { getSwitchesAlreadyOnVehicle } from '../utils'
import { ArticleStyled } from '../AddRemoveVehicleFeatures.style'
import { StyledList } from './CommitView.style'

const renderList = (list, title) =>
  !isEmpty(list) && (
    <Row>
      <Col span={12}>
        <Paragraph weight="bold">{title}</Paragraph>
        <StyledList>
          {sortBy((item) => {
            // Use panelNumber if it exists, otherwise fall back to partNumber
            const { panelNumber } = item
            return panelNumber
              ? parseInt(panelNumber.replace(/\D/g, ''), 10) // If panelNumber exists, extract and convert to number
              : item.partNumber // If panelNumber doesn't exist, use partNumber
          })(list).map(renderListItem)}
        </StyledList>
        <br />
      </Col>
    </Row>
  )
const renderListItem = (item) => {
  const id =
    propOr(null, 'panelNumber', item) || propOr(null, 'partNumber', item)
  const description =
    propOr(null, 'description', item) || propOr(null, 'panelDescription', item)
  const revision = propOr(null, 'revision', item)

  // Format: "ID, Revision X: description" or "ID: description" if no revision
  const displayId = revision ? `${id}, ${strings.revision} ${revision}` : id

  return (
    <li key={id}>
      <span>{displayId}:</span> <span>{description}</span>
    </li>
  )
}

const CommitView = ({
  updated,
  added,
  removed,
  commitMessage,
  onCommitMessageChange,
  addedIOSwitchesFromUpdate,
  removedIOSwitchesFromUpdate,
  mappedIoSwitches,
  keepOrRemoveSwitches = [],
  serialNumber,
  showNewSolenoidsList,
}) => {
  const dispatch = useDispatch()
  const solenoids = useSelector(getAllSolenoids)
  const solenoidListOnVehicle = useSelector(solenoidListOnVehicleSelector)
  const vehicleFeatures = useSelector((state) =>
    getTruckFeatures(state, { serialNumber })
  )

  const panelNumbersOnVehicle = useMemo(() => {
    const panels = new Set()
    ;(vehicleFeatures || []).forEach((feature) => {
      ;(feature.ioSwitches || []).forEach((io) => {
        ;(io.switches || []).forEach((sw) => {
          if (sw.isSwitchOnVehicle && sw.panelNumber) {
            panels.add(sw.panelNumber)
          }
        })
      })
    })
    return panels
  }, [vehicleFeatures])

  const candidateSwitchPanels = useMemo(() => {
    const switches = [
      ...getAddedSwitches(added, mappedIoSwitches),
      ...getAddedSwitches(updated, addedIOSwitchesFromUpdate, true),
    ]
    return [...new Set(switches.map((sw) => sw.panelNumber).filter(Boolean))]
  }, [added, mappedIoSwitches, updated, addedIOSwitchesFromUpdate])

  const candidatePanelsKey = useMemo(
    () => JSON.stringify([...candidateSwitchPanels].sort()),
    [candidateSwitchPanels]
  )

  const [serverConfirmedOnVehicle, setServerConfirmedOnVehicle] = useState([])

  useEffect(() => {
    let cancelled = false
    getSwitchesAlreadyOnVehicle(dispatch, serialNumber, candidateSwitchPanels)
      .then((result) => {
        if (!cancelled) {
          // Always check result.ok — a de-duplicated call returns
          // { panels: [], ok: false }, which means the validation result is
          // unknown (another call is in-flight). In that case do not update
          // serverConfirmedOnVehicle so the UI keeps its previous state rather
          // than silently showing an empty panel list.
          if (result.ok) {
            setServerConfirmedOnVehicle(result.panels)
          }
        }
      })
      .catch(() => {
        // Network/server errors from getSwitchesAlreadyOnVehicle propagate to
        // callers (documented in utils.js). Fallback to an empty array so the
        // UI pre-check shows no server-confirmed panels rather than hanging on
        // stale data. This is safe — it's a display-only pre-check, not a hard
        // save guard.
        if (!cancelled) setServerConfirmedOnVehicle([])
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialNumber, candidatePanelsKey])

  const allPanelsOnVehicle = useMemo(() => {
    const panels = new Set(panelNumbersOnVehicle)
    serverConfirmedOnVehicle.forEach((p) => panels.add(p))
    return panels
  }, [panelNumbersOnVehicle, serverConfirmedOnVehicle])

  const addedSwitchList = useMemo(
    () =>
      getAddedSwitches(added, mappedIoSwitches).filter(
        (sw) => !allPanelsOnVehicle.has(sw.panelNumber)
      ),
    [added, mappedIoSwitches, allPanelsOnVehicle]
  )
  const removedSwitchesList = useMemo(
    () => getRemovedSwitches(removed, keepOrRemoveSwitches),
    [removed, keepOrRemoveSwitches]
  )

  const addedSwitchesListFromUpdatedFeatures = useMemo(
    () =>
      getAddedSwitches(updated, addedIOSwitchesFromUpdate, true).filter(
        (sw) => !allPanelsOnVehicle.has(sw.panelNumber)
      ),
    [updated, addedIOSwitchesFromUpdate, allPanelsOnVehicle]
  )
  const removedSwitchesListFromUpdatedFeatures = useMemo(
    () =>
      getRemovedSwitchesFromPrevSnapshot(updated, removedIOSwitchesFromUpdate),
    [updated, removedIOSwitchesFromUpdate]
  )

  const optionalSolenoidsList = useMemo(
    () =>
      added
        .reduce((prev, cur) => {
          const solenoidsList = cur.solenoidIds
            ?.map((item) =>
              !prev.find((y) => y.partNumber === solenoids[item]?.partNumber)
                ? {
                    partNumber: solenoids[item]?.partNumber,
                    description: solenoids[item]?.function,
                    id: item,
                  }
                : null
            )
            .filter((item) => item)
          return [...prev, ...solenoidsList]
        }, [])
        .filter((item) => !solenoidListOnVehicle.includes(item.id)),
    [added, solenoidListOnVehicle, solenoids]
  )

  const switchesListTitle = (
    <>
      {strings.the_following_switches_will_be_added}
      <br />
      <span style={{ fontWeight: 'normal', fontStyle: 'italic' }}>
        {strings.you_can_assign_locations_from}
      </span>
    </>
  )

  const asBuiltRemoved = removed.filter(propEq('asBuilt', true))
  const asServicedRemoved = removed.filter(propEq('asBuilt', false))

  return (
    <ArticleStyled>
      {Boolean(length(asBuiltRemoved)) && (
        <>
          <Alert
            message={strings.removing_as_built_features_notification}
            type="warning"
          />
        </>
      )}
      <Header type="h3">
        {strings.you_are_about_to_commit_changes_to_vehicle(serialNumber)}
      </Header>
      <br />
      {renderList(updated, strings.new_version_commit_title)}
      {renderList(added, strings.adding_commit_title)}
      {renderList(asBuiltRemoved, strings.removing_as_built_features)}
      {renderList(asServicedRemoved, strings.removing_commit_title)}
      {(addedSwitchList || addedSwitchesListFromUpdatedFeatures) &&
        renderList(
          [...addedSwitchList, ...addedSwitchesListFromUpdatedFeatures],
          switchesListTitle
        )}
      {(removedSwitchesList || removedSwitchesListFromUpdatedFeatures) &&
        renderList(
          [...removedSwitchesList, ...removedSwitchesListFromUpdatedFeatures],
          strings.the_following_switches_will_be_removed
        )}
      {showNewSolenoidsList &&
        renderList(
          optionalSolenoidsList,
          strings.optional_solenoids_commit_view_title
        )}
      <Row>
        <Col span={12}>
          {strings.notes_required_star}
          <TextArea
            placeholder={strings.notes_required}
            value={commitMessage}
            onChange={onCommitMessageChange}
            type="textarea"
            rows={4}
            maxLength={1000}
            charsRemainingNumberOnly
          />
        </Col>
      </Row>
    </ArticleStyled>
  )
}

CommitView.propTypes = {
  updated: PropTypes.array,
  added: PropTypes.array,
  removed: PropTypes.array,
  addedSwitches: PropTypes.object,
  removedIOSwitchesFromUpdate: PropTypes.object,
  addedIOSwitchesFromUpdate: PropTypes.object,
  commitMessage: PropTypes.string,
  onCommitMessageChange: PropTypes.func,
  mappedIoSwitches: PropTypes.object,
  keepOrRemoveSwitches: PropTypes.array,
  serialNumber: PropTypes.string,
  showNewSolenoidsList: PropTypes.bool,
}

export default CommitView
